import videojs from "video.js";
import Timer from "@utilities/Timer";
import xPath from "@utilities/XPath";
import parseTime from "@utilities/ParseTime";

import "./Plugin.scss";
import AdBreakTimeRemaining from "./Components/AdBreakTimeRemaining";

const logger = videojs.createLogger("AdTriggerMetadata");

const _maxAdRetrySeconds = 120;

// Default options for the plugin.
const defaults = {
	enabled: true,
	minAdDuration: 6,
	vmapOverride: null
};

/**
 * A basic Video.js plugin.
 *
 * We want to update this later, but for now, the logic is as follows:
 *
 * 1. Keep ONE ad "preloaded" at all times
 *     - Do NOT load this ad on the adBreakTimer tick. Load it when the plugin
 *       first loads, and as described below
 * 2. When we see adBreakStart(offset=0) or adBreakEnd(offset!=0), we start the
 *    adBreakTimer (or restart it, if already started) and display the
 *    remainingTimeDisplay. Also, add this adBreak to a list of known adBreaks
 *    so we can skip it if we see it again (seeking backwards should not trigger
 *    more ads)
 * 3. When the adBreakTimer ticks each second:
 *     - We update the remainingTimeDisplay
 *     - We try to play the preloaded ad
 * 4. When the adBreakTimer ends, we kill it and remove the remainingTimeDisplay
 */
function Metadata(options)
{
	const player = this;
	const opts = videojs.mergeOptions(defaults, options);

	if (!opts.enabled)
	{
		return;
	}

	// One ad actively playing
	let playingAd = null;

	// And one preloaded
	let preloadedAd = null;

	// And a timer if it fails to load
	let adLoadTimeout = null;
	let adRetrySeconds = 1;

	// VMAP AdBreaks
	const adBreaks = [];
	let currentAdBreak = 0;

	// Timer to handle AdBreak Mode
	let inAdBreak = false;
	const adBreakTimer = new Timer(1, 1, handleTick);
	player.on("metadata", handleMetadata);

	// On iOS we use the content player to play ads
	// This means that after an ad finishes playing, or after an adbreak ends,
	// we need to revert the content player to its prior state (plus add some
	// time to the currentTime based on how long we've been playing)
	let preAdTime = 0;

	// If the user JUST finished an ad and seeks back, we shouldn't play
	// more ads. We'll just give them the splash screen
	// TODO: We should have a configurable timeout after which we will play
	// TODO: the midroll again
	let playedAdBreaks = [];
	player.on("contentchanged", () =>
	{
		logger("Content changed. Forgetting past ad breaks.");
		playedAdBreaks = [];
	});

	if (opts.vmapOverride)
	{
		fetch(opts.vmapOverride)
			.then(resp => resp.text())
			.then(resp => xPath(resp, "//VMAP").iterateNext())
			.then(parseVMAP);
	}
	else
	{
		player.on("vmap-ready", () => { parseVMAP(player.vmap); });
	}

	const remainingTimeDisplay = new AdBreakTimeRemaining(player, adBreakTimeRemaining, options);
	player.addChild(remainingTimeDisplay);

	player.on("play", () =>
	{
		if (inAdBreak)
		{
			logger.debug("Play during adbreak");
			adBreakTimer.start();
		}
	});
	player.on("pause", () =>
	{
		if (inAdBreak)
		{
			logger.debug("Paused during adbreak");
			adBreakTimer.stop();
		}
	});

	function addAdBreakToList(start, duration)
	{
		const end = start + duration;
		for (let i = 0; i < playedAdBreaks.length; i++)
		{
			if (
				start >= playedAdBreaks[i].start &&
				end <= playedAdBreaks[i].end)
			{
				// The adbreak in question is entirely within adbreak "i"
				return;
			}

			if (
				start >= playedAdBreaks[i].start &&
				start <= playedAdBreaks[i].end
			)
			{
				// The adbreak in question started in the middle of adbreak "i"
				playedAdBreaks[i].end = Math.ceil(end);
				return;
			}

			if (
				end >= playedAdBreaks[i].start &&
				end <= playedAdBreaks[i].end
			)
			{
				// The adbreak in question ended in the middle of adbreak "i"
				playedAdBreaks[i].start = Math.floor(start);
				return;
			}
		}

		// If we got here, this adbreak must be completely separate from
		// existing adbreaks in our array. Let's add it.
		playedAdBreaks.push({
			start: Math.floor(start),
			end: Math.ceil(end)
		});
	}

	function haveSeenAdBreak(start)
	{
		return playedAdBreaks.reduce((seen, adbreak) =>
		{
			return seen || (start >= adbreak.start && start <= adbreak.end);
		}, false);
	}

	function beginAdBreak(duration)
	{
		// TODO: not all of this needs to happen for every new piece of metadata
		// TODO: but it is proving to be more complex then just using a early return(bec of how the timers are set up.).
		// TODO: and not entire necessary to fix this *now*
		// if (inAdBreak) return;

		if (!inAdBreak && haveSeenAdBreak(player.currentTime()))
		{
			// Don't play an ad break we've already seen
			logger("AdBreak already hit. Not entering ad mode");
			return;
		}

		if (!inAdBreak)
		{
			preAdTime = player.currentTime();
			logger(`Setting preAdTime = ${preAdTime}`);
			player.ads.startLinearAdMode();

			adBreakTimer.reset().setDuration(duration).start();
		}
		else if (!videojs.browser.IS_IOS)
		{
			// we want to reset the timer every time on desktop/android but not ios
			adBreakTimer.reset().setDuration(duration).start();
		}

		addAdBreakToList(player.currentTime(), duration);

		remainingTimeDisplay.show();
		remainingTimeDisplay.update();

		if (!inAdBreak)
		{
			tryToPlayAnAd();
		}

		inAdBreak = true;
	}

	function handleTick()
	{
		const remainingTime = adBreakTimeRemaining();
		logger.debug(`adBreakTimer tick. Time remaining: ${Math.round(remainingTime)}`);
		if (remainingTime <= 1)
		{
			endAdBreak();
		}
		else
		{
			remainingTimeDisplay.update();
			// console.log("handling tick")
			tryToPlayAnAd();
		}
	}

	function endAdBreak()
	{
		logger("Exiting linear ad mode");

		remainingTimeDisplay.hide();
		adBreakTimer.stop();

		if (playingAd)
		{
			logger("An ad is currently playing. Killing it early.");
			playingAd.destroy();
			playingAd = null;
		}

		player.ads.endLinearAdMode();
		if (videojs.browser.IS_IOS && inAdBreak)
		{
			logger("Binding playing event handler");
			player.one("playing", () =>
			{
				logger(`Updating currentTime to preAdTime (${preAdTime}) + runningAdTime (${adBreakTimer.elapsedTime()})`);
				player.currentTime(preAdTime + adBreakTimer.elapsedTime());
			});

			player.play();
		}

		inAdBreak = false;
	}

	function playPreloadedAd()
	{
		const isPreloaded = preloadedAd ? preloadedAd.loaded : false;
		if (!playingAd && isPreloaded)
		{
			logger("Playing an ad.");
			preloadedAd.play();
			playingAd = preloadedAd;
			preloadedAd = null;

			// Load a replacement
			preloadAd();
		}
	}

	function tryToPlayAnAd()
	{
		// We are already playing an ad (don't play it yet)
		if (playingAd)
		{
			logger.debug("Not playing ad. An ad is already playing.");
			return;
		}

		if (!inAdBreak)
		{
			logger.debug("Not playing ad. We're not in an adbreak.");
			return;
		}

		if (!preloadedAd)
		{
			if (
				videojs.browser.IS_IOS &&
				Math.abs(
					player.currentTime() - (preAdTime + adBreakTimer.elapsedTime())
				) > 1
			)
			{
				logger(`Updating currentTime to preAdTime (${preAdTime}) + runningAdTime (${adBreakTimer.elapsedTime()})`);
				player.currentTime(preAdTime + adBreakTimer.elapsedTime());
			}
			if (adLoadTimeout)
			{
				logger.debug("Not playing ad. We're waiting for a new one to load.");
				return;
			}
			else
			{
				// Bug
				logger.warn("No ad was ready. Emergency ad load now.");
				preloadAd();
			}
		}

		const timeRemaining = adBreakTimeRemaining();
		if (timeRemaining < opts.minAdDuration)
		{
			// Just show the splash screen for the last few seconds
			logger.debug(`Not playing ad. Only ${timeRemaining} seconds remain.`);
			// On iOS we may need to seek back to the splash screen
			if (videojs.browser.IS_IOS && inAdBreak)
			{
				logger("Binding playing event handler");
				player.one("playing", () =>
				{
					logger(`Updating currentTime to preAdTime (${preAdTime}) + runningAdTime (${adBreakTimer.elapsedTime()})`);
					player.currentTime(preAdTime + adBreakTimer.elapsedTime());
				});
				endAdBreak();
			}
			return;
		}

		playPreloadedAd();
	}

	function adBreakTimeRemaining()
	{
		return adBreakTimer.remainingTime();
	}

	function preloadAd()
	{
		if (adBreaks.length === 0)
		{
			// There are no ads to load... Why are we even here?
			return;
		}

		const adTagURI = getAdTagURI(adBreaks[currentAdBreak++]);

		if (adTagURI === null)
		{
			// We failed to find an ad
			return;
		}

		if (currentAdBreak >= adBreaks.length)
			currentAdBreak = 0;

		const newAd = player.blueframeAds().load(adTagURI, {
			name: "Midroll"
		});
		newAd.load();

		if (!newAd)
		{
			// Ads are disabled
			return;
		}

		if (preloadedAd)
		{
			logger.warn("Preloaded ad while we already had one ready... Destroying old ad.");
			logger("Destroying preloaded ad (due to preload)");
			preloadedAd.destroy();
		}

		newAd.on("complete", handleEnded);
		newAd.on("error", handleAdError);

		logger.debug(`Bound event handlers to ad [${newAd.name}]`);

		preloadedAd = newAd;
	}

	function handleEnded()
	{
		logger.debug("Ad ended");

		adRetrySeconds = 1;
		playingAd = null;
		// Timer is paused on IOS when an ad ends(vjs sends a pause trigger before the ended trigger.). lets restart it.
		adBreakTimer.start();
		tryToPlayAnAd();
	}

	function handleAdError(event)
	{
		const errorCode = event.getError().getErrorCode();
		const errorCodeLookup = preloadedAd.ErrorCodes;
		logger("Destroying preloaded ad (due to error)");
		preloadedAd.destroy();
		preloadedAd = null;

		if (errorCode === errorCodeLookup.EmptyVASTResponse)
		{
			logger(`Empty VAST. Delaying for ${adRetrySeconds} second(s).`);
			adLoadTimeout = setTimeout(preloadAd, adRetrySeconds * 1000);
			if (adRetrySeconds < _maxAdRetrySeconds)
			{
				adRetrySeconds = Math.min(_maxAdRetrySeconds, adRetrySeconds * 2);
			}
		}
		else if (errorCode === errorCodeLookup.VASTTimeout || errorCode === errorCodeLookup.MediaTimeout)
		{
			logger("Ad timeout. Retrying immediately.");
			adLoadTimeout = setTimeout(preloadAd, 1);
		}
		else
		{
			logger.error(`AdError: ${errorCode}... How to handle?`);
			adLoadTimeout = setTimeout(preloadAd, 1000);
		}
	}

	function getAdTagURI(adBreak)
	{
		const adSources = xPath(
			adBreak,
			"//Extensions/Extension[@type='VolarAdBreakExtension']"
		);

		const isMobile = videojs.browser.IS_IOS || videojs.browser.IS_ANDROID;
		const sources = [];
		let sumWeight = 0;

		let next = adSources.iterateNext();

		if (next === null)
		{
			// We didn't have a single VolarAdBreakExtension
			// But maybe this isn't a VolarVMAP (ad tag override)
			// Let's see if it's a normal AdBreak
			const adSource = xPath(adBreak, "//AdSource").iterateNext();
			if (adSource !== null)
			{
				sources.push({
					weight: 1,
					uri: xPath(adSource, "//AdTagURI").iterateNext().textContent
				});
			}
		}
		else
		{
			while (next)
			{
				const roll = xPath(next, "//Roll").iterateNext();
				if (roll.textContent === "mid-roll")
				{
					const source = xPath(next, "//AdSource").iterateNext();

					const platform = source.getAttribute("platform") || "all";
					const weight = parseFloat(source.getAttribute("weight")) || 1;

					if (
						(platform === "mobile" && isMobile) ||
						(platform === "desktop" && !isMobile) ||
						platform === "all"
					)
					{
						sources.push({
							weight: weight,
							uri: xPath(next, "//AdTagURI").iterateNext().textContent
						});
						sumWeight += weight;
					}
				}

				next = adSources.iterateNext();
			}
		}

		if (sources.length === 0)
		{
			return null;
		}

		const selected = Math.random() * sumWeight;
		let selectedSource = null;

		for (let i = 0; i < sources.length; i++)
		{
			sumWeight -= sources[i].weight;
			if (sumWeight <= selected)
			{
				selectedSource = sources[i];
				break;
			}
		}

		return selectedSource.uri;
	}

	function parseVMAP(vmap)
	{
		const adBreakTags = xPath(vmap, "//AdBreak");

		if (adBreakTags.length === 0)
		{
			logger.error("There are no ad tags in the VMAP -- cannot play ads");
			return;
		}

		let next = adBreakTags.iterateNext();
		while (next)
		{
			// If this is not a pre-roll or a post-roll, we want to add this
			// AdBreak as a possible source for ad tags
			if (parseTime(next.getAttribute("timeOffset")).roll === "midroll")
			{
				adBreaks.push(next);
			}
			next = adBreakTags.iterateNext();
		}

		if (adBreaks.length === 0)
		{
			// There were no mid-rolls in the VMAP, let's just grab the first
			// VAST we see and use that for all mid-rolls (probably a pre-roll)
			const firstAdBreak = xPath(vmap, "//AdBreak").iterateNext();
			if (firstAdBreak)
			{
				adBreaks.push(firstAdBreak);
			}
		}

		// Wait to pre-load the midroll when the player starts
		// This way we don't pre-load a midroll until all pre-rolls are done
		player.one("playing", () =>
		{
			player.one("timeupdate", preloadAd);
		});
	}

	function handleMetadata(event, data)
	{
		// We dont want to act on metadata while we are seeking.
		// Since the player is paused while seeking so this will prevent
		// an adbreak from triggering while seeking.
		// This has the side effect of making it easier for users to skip ads on archived broadcast.
		// The alternative is the play completely breaks and is unuseable until a refreash if they seek into an ad break.
		if (player.paused()) return;

		if (Array.isArray(data) && data.length >= 2 && data[0] === "onVolarData")
		{
			// console.log(data[1]);
			onVolarData(data[1]);
		}
	}

	function onVolarData(data)
	{
		if (data.event === "adBreakStart")
		{
			if (data.offset === 0)
			{
				beginAdBreak(data.duration);
			}
		}
		else if (data.event === "adBreakEnd" && data.offset >= 1)
		{
			beginAdBreak(data.offset);
		}
		else if (data.event === "adBreakEnd")
		{
			endAdBreak();
		}
	}
}

// Register the plugin with video.js.
videojs.registerPlugin("adTriggerMetadata", Metadata);
